/**
 * 用戶數據轉換器
 * 處理後端返回的用戶數據與前端期望格式的轉換
 */

import type { User } from '@/types/api'

/**
 * 後端返回的原始用戶數據格式
 */
interface RawUserData {
  id: string
  telegramId?: string | number
  firstName: string
  lastName?: string
  username?: string
  email?: string
  phone?: string
  address?: string
  photoUrl?: string
  status?: string
  role: 'student' | 'teacher' | 'admin'
  isActive: boolean
  birthYear?: string | number
  birthMonth?: string | number
  dateOfBirth?: string
  height?: string | number
  weight?: string | number
  gender?: string
  size?: string
  bio?: string
  createdAt: string
  updatedAt: string
  authDate?: string
}

/**
 * 將字符串轉換為數字，如果無法轉換則返回 undefined
 */
function parseNumber(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    // 忽略明顯的佔位符字符串
    if (value === 'birth_year' || value === 'birth_month' || value === 'height' || value === 'weight') {
      return undefined
    }
    const parsed = parseInt(value, 10)
    return isNaN(parsed) ? undefined : parsed
  }
  return undefined
}

/**
 * 標準化狀態值
 */
function normalizeStatus(status: string | undefined): User['status'] {
  if (!status || status === 'status') return 'active' // 默認狀態
  
  const validStatuses: User['status'][] = ['active', 'pending', 'banned', 'suspended', 'leaved', 'inactive']
  if (validStatuses.includes(status as User['status'])) {
    return status as User['status']
  }
  
  return 'active' // 默認狀態
}

/**
 * 標準化性別值
 */
function normalizeGender(gender: string | undefined): User['gender'] {
  if (!gender || gender === 'gender') return undefined
  
  const validGenders: User['gender'][] = ['male', 'female', 'other']
  if (validGenders.includes(gender as User['gender'])) {
    return gender as User['gender']
  }
  
  return undefined
}

/**
 * 標準化字符串字段
 */
function normalizeString(value: string | undefined, placeholder?: string): string | undefined {
  if (!value || value === placeholder) return undefined
  return value.trim() || undefined
}

/**
 * 將後端原始用戶數據轉換為前端期望的格式
 */
export function mapUserData(rawData: RawUserData): User {
  console.log('[UserMapper] Raw data:', rawData)
  
  const mappedUser: User = {
    id: rawData.id,
    telegramId: rawData.telegramId,
    firstName: rawData.firstName,
    lastName: normalizeString(rawData.lastName),
    username: normalizeString(rawData.username),
    email: normalizeString(rawData.email, 'email'),
    phone: normalizeString(rawData.phone, 'phone'),
    address: normalizeString(rawData.address, 'address'),
    photoUrl: rawData.photoUrl,
    status: normalizeStatus(rawData.status),
    role: rawData.role,
    isActive: rawData.isActive,
    birthYear: parseNumber(rawData.birthYear),
    birthMonth: parseNumber(rawData.birthMonth),
    dateOfBirth: normalizeString(rawData.dateOfBirth, 'date_of_birth'),
    height: parseNumber(rawData.height),
    weight: parseNumber(rawData.weight),
    gender: normalizeGender(rawData.gender),
    size: normalizeString(rawData.size, 'size'),
    bio: normalizeString(rawData.bio, 'bio'),
    createdAt: rawData.createdAt,
    updatedAt: rawData.updatedAt
  }
  
  console.log('[UserMapper] Mapped user:', mappedUser)
  return mappedUser
}

/**
 * 批量轉換用戶數據
 */
export function mapUsersData(rawUsers: RawUserData[]): User[] {
  return rawUsers.map(mapUserData)
}